<script>
  import BeforeAfterSlider from './BeforeAfterSlider.svelte';
  import './glass.css';

  let currentIndex = 0;
  let isPaused = false;

  const comparisons = [
    { id: 1, title: 'Dentobot-Clear Treatment Outcomes', before: '/public/assets/Capture1.JPG', after: '/public/assets/Capture1_2.JPG', description: 'Professional teeth whitening showing dramatic results in just one session.' },
    { id: 2, title: 'Dentobot-Clear Treatment Outcomes', before: '/public/assets/Capture2.JPG', after: '/public/assets/Capture2_2.JPG', description: 'Complete smile makeover with ceramic veneers and crowns.' },
    { id: 3, title: 'Dentobot-Clear Treatment Outcomes', before: '/public/assets/Capture3.JPG', after: '/public/assets/Capture3_1.JPG', description: 'Straight, beautiful smile achieved through modern orthodontic techniques.' },
  ];
 
  let intervalId;
  $: if (!isPaused) {
    clearInterval(intervalId);
    intervalId = setInterval(() => {
      currentIndex = currentIndex === comparisons.length - 1 ? 0 : currentIndex + 1;
    }, 5000);
  } else {
    clearInterval(intervalId);
  }

  function goToSlide(index) {
    currentIndex = index;
  }
  function goToPrevious() {
    currentIndex = currentIndex === 0 ? comparisons.length - 1 : currentIndex - 1;
  }
  function goToNext() {
    currentIndex = currentIndex === comparisons.length - 1 ? 0 : currentIndex + 1;
  }

  $: currentComparison = comparisons[currentIndex];
</script>

<section class="py-16 bg-white relative overflow-hidden">
  <!-- Floating glass orbs for ambiance -->
  <div class="absolute inset-0 overflow-hidden pointer-events-none">
    <div class="floating-orb absolute top-20 left-20 w-32 h-32 bg-white/5 backdrop-blur-sm rounded-full animate-float-slow"></div>
    <div class="floating-orb absolute top-40 right-32 w-24 h-24 bg-blue-200/10 backdrop-blur-sm rounded-full animate-float-medium"></div>
    <div class="floating-orb absolute bottom-32 left-1/3 w-20 h-20 bg-indigo-200/8 backdrop-blur-sm rounded-full animate-float-fast"></div>
  </div>

  <div class="container mx-auto px-4 relative z-10">
    <div class="max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto">
      <div 
        class="glass-container overflow-hidden bg-white/25 backdrop-blur-lg border border-white/40 rounded-3xl shadow-2xl hover:shadow-3xl hover:bg-white/30 transition-all duration-500 transform hover:-translate-y-2"
        on:mouseenter={() => isPaused = true}
        on:mouseleave={() => isPaused = false}
        role="region"
      >
        <!-- Liquid gradient overlay -->
        <div class="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-blue-500/5 pointer-events-none rounded-3xl"></div>
        
        <div class="p-4 sm:p-6 md:p-8 relative z-10">
          <!-- Title and description section -->
          <div class="text-center mb-8">
            <div class="glass-card inline-block px-8 py-6 mb-6 bg-white/20 backdrop-blur-md border border-white/30 rounded-3xl shadow-lg">
              <h2 class="text-4xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
                Before & After Results
              </h2>
              <p class="text-xl text-gray-700 max-w-3xl mx-auto font-medium">
                See the amazing transformations our patients have achieved. 
                Drag the slider to compare before and after images.
              </p>
            </div>
            
            <!-- Current comparison title and description -->
            <h3 class="text-xl sm:text-2xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-[#4eb4c4] to-[#4eb4c4]">
              {currentComparison.title}
            </h3>
            <p class="text-sm sm:text-base font-medium text-[#4eb4c4]">
              {currentComparison.description}
            </p>
          </div>

          <div class="relative">
            <BeforeAfterSlider
              beforeImage={currentComparison.before}
              afterImage={currentComparison.after}
              className="mb-4 sm:mb-6"
            />

            <!-- Liquid glass navigation buttons -->
            <button
              on:click={goToPrevious}
              class="nav-button absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white/25 backdrop-blur-md border border-white/40 hover:bg-white/35 text-gray-800 rounded-full p-2 sm:p-3 shadow-xl transition-all duration-300 hover:scale-110 hover:shadow-2xl -ml-12 liquid-glow"
              aria-label="Previous image"
            >
              <svg class="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              on:click={goToNext}
              class="nav-button absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white/25 backdrop-blur-md border border-white/40 hover:bg-white/35 text-gray-800 rounded-full p-2 sm:p-3 shadow-xl transition-all duration-300 hover:scale-110 hover:shadow-2xl -mr-12 liquid-glow"
              aria-label="Next image"
            >
              <svg class="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <!-- Glass morphism dot indicators -->
          <div class="flex justify-center space-x-2 sm:space-x-3 mt-4 sm:mt-6">
            <div class="glass-dots bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-2 inline-flex space-x-2 sm:space-x-3">
              {#each comparisons as _, index}
                <button
                  on:click={() => goToSlide(index)}
                  class={`dot-indicator w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 scale-125 shadow-lg animate-pulse-subtle' 
                      : 'bg-white/40 hover:bg-white/60 backdrop-blur-sm'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              {/each}
            </div>
          </div>
        </div>

        <!-- Liquid shimmer effect -->
        <div class="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
          <div class="liquid-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 animate-shimmer"></div>
        </div>
      </div>
    </div>
  </div>
</section>